import React, { useState, useRef, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { MLB_TEAMS } from '../../../lib/mlb/teamCodes';

export const TEAM_LABELS = Object.fromEntries(MLB_TEAMS.map((t) => [t.slug, t.name]));

export default function TeamPicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [highlightIdx, setHighlightIdx] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const wrapperRef = useRef(null);

  const filtered = useMemo(() => {
    if (!search) return MLB_TEAMS;
    const q = search.toLowerCase();
    return MLB_TEAMS.filter(
      (t) => t.name.toLowerCase().includes(q)
        || t.abbr.toLowerCase().includes(q)
        || t.slug.toLowerCase().includes(q),
    );
  }, [search]);

  useEffect(() => { setHighlightIdx(0); }, [filtered]);

  useEffect(() => {
    if (!open) return undefined;
    // The listbox is portaled to document.body, so it is NOT a descendant of
    // wrapperRef. We must check both refs — otherwise every option click
    // registers as "outside", the dropdown unmounts on mouseup, and the
    // option's onClick never fires (the bug the user reported repeatedly).
    function handleClickOutside(e) {
      const inWrapper = wrapperRef.current && wrapperRef.current.contains(e.target);
      const inList = listRef.current && listRef.current.contains(e.target);
      if (!inWrapper && !inList) {
        setOpen(false);
      }
    }
    document.addEventListener('mouseup', handleClickOutside);
    return () => document.removeEventListener('mouseup', handleClickOutside);
  }, [open]);

  useEffect(() => {
    if (open && listRef.current) {
      const active = listRef.current.querySelector('[data-highlighted="true"]');
      if (active) active.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightIdx, open]);

  function select(team) {
    onChange(team.slug);
    setSearch('');
    setOpen(false);
    inputRef.current?.blur();
  }

  function handleKeyDown(e) {
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) {
      setOpen(true);
      e.preventDefault();
      return;
    }
    if (!open) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIdx((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filtered[highlightIdx]) select(filtered[highlightIdx]);
    } else if (e.key === 'Escape') {
      setOpen(false);
      setSearch('');
    }
  }

  const current = MLB_TEAMS.find((t) => t.slug === value);
  const displayText = current ? current.name : value;

  // Position the portaled dropdown directly below the input. Recompute on
  // open and on resize/scroll so it tracks the trigger when the layout shifts.
  const [rect, setRect] = useState(null);
  useEffect(() => {
    if (!open) return undefined;
    function recalc() {
      const el = inputRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setRect({ left: r.left, top: r.bottom, width: r.width });
    }
    recalc();
    window.addEventListener('resize', recalc);
    window.addEventListener('scroll', recalc, true);
    return () => {
      window.removeEventListener('resize', recalc);
      window.removeEventListener('scroll', recalc, true);
    };
  }, [open]);

  return (
    <div ref={wrapperRef} style={{ position: 'relative', minWidth: '240px' }}>
      <label style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <span style={{
          fontSize: '0.75rem', fontWeight: 600, letterSpacing: '0.1em',
          textTransform: 'uppercase', color: 'var(--text-quaternary)',
        }}>
          Team
        </span>
        <input
          ref={inputRef}
          role="combobox"
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-label="Select team"
          value={open ? search : displayText}
          onChange={(e) => { setSearch(e.target.value); setOpen(true); }}
          onFocus={() => { setOpen(true); setSearch(''); }}
          onKeyDown={handleKeyDown}
          placeholder="Search teams…"
          style={{
            background: '#1c2026', color: 'var(--text-primary, #dfe2eb)',
            border: '1px solid rgba(255,255,255,0.08)', borderRadius: 'var(--radius-sm, 4px)',
            padding: '6px 10px', fontSize: '0.875rem',
            fontFamily: 'var(--font-family-body)', cursor: 'pointer',
            width: '100%', outline: 'none',
          }}
        />
      </label>

      {open && rect && ReactDOM.createPortal(
        <ul
          ref={listRef}
          role="listbox"
          style={{
            position: 'fixed',
            top: rect.top + 4,
            left: rect.left,
            width: rect.width,
            zIndex: 1000,
            margin: 0,
            padding: '4px 0',
            maxHeight: '320px',
            overflowY: 'auto',
            background: '#181c22',
            border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: 6,
            listStyle: 'none',
            boxShadow: '0 12px 32px rgba(0,0,0,0.6)',
          }}
        >
          {filtered.length === 0 && (
            <li style={{ padding: '8px 12px', color: '#858d9c', fontSize: '0.8125rem' }}>
              No teams match "{search}"
            </li>
          )}
          {filtered.map((team, i) => (
            <li
              key={team.slug}
              role="option"
              aria-selected={team.slug === value}
              data-highlighted={i === highlightIdx ? 'true' : undefined}
              onClick={() => select(team)}
              onMouseEnter={() => setHighlightIdx(i)}
              style={{
                padding: '8px 12px', cursor: 'pointer', display: 'flex',
                alignItems: 'center', gap: '10px', fontSize: '0.875rem',
                background: i === highlightIdx ? 'rgba(254,214,3,0.08)' : 'transparent',
                color: team.slug === value ? '#FED603' : '#dfe2eb',
                fontWeight: team.slug === value ? 600 : 400,
                borderLeft: team.slug === value ? '2px solid #FED603' : '2px solid transparent',
              }}
            >
              <span style={{
                width: '10px', height: '10px', borderRadius: '50%',
                background: team.color, flexShrink: 0,
              }} />
              <span>{team.name}</span>
              <span style={{ marginLeft: 'auto', fontSize: '0.6875rem', color: '#858d9c' }}>
                {team.abbr}
              </span>
            </li>
          ))}
        </ul>,
        document.body,
      )}
    </div>
  );
}
